import { getDefaultStore } from "jotai";
import { isSegmentationActiveAtom } from "../atoms/segmentationAtoms";
import { copyContourForCheckpoint } from "../utils/segmentationGeometryCheckpoint";
import { getActiveScope, pushUndo } from "./annotationUndo";
import { refreshViewerFromAnnotations } from "./nnInteractiveSession";

// Endpoint edits can emit COMPLETED too. They must not also queue removal as
// though the user had drawn a new contour.
const editingContours = new WeakSet<object>();
export function isLassoContourEdit(annotation: object): boolean {
  return editingContours.has(annotation);
}

/** Keep Cornerstone's edit behavior; add history only in segmentation sessions. */
export function createSegmentationLassoToolClass(cornerstoneTools: any): any {
  return class SegmentationLassoTool
    extends cornerstoneTools.PlanarFreehandContourSegmentationTool
  {
    constructor(...args: any[]) {
      super(...args);
      let pending: {
        annotation: any;
        before: any;
        element: HTMLElement;
        scope: NonNullable<ReturnType<typeof getActiveScope>>;
      } | null = null;
      const clear = () => {
        if (pending) editingContours.delete(pending.annotation);
        pending = null;
      };
      const finish = () => {
        const edit = pending;
        clear();
        if (!edit) return;
        const scope = getActiveScope();
        if (
          !getDefaultStore().get(isSegmentationActiveAtom) ||
          scope?.session !== edit.scope.session ||
          scope.studyUid !== edit.scope.studyUid
        )
          return;
        if (
          JSON.stringify(edit.before.data.contour) === JSON.stringify(edit.annotation.data.contour)
        )
          return;
        const uid = edit.before.annotationUID;
        pushUndo({
          kind: "lassoRevert",
          undo: () => {
            // NN/threshold Undo may have recreated the object under the same ID.
            const live = cornerstoneTools.annotation.state.getAnnotation(uid);
            if (!live) return;
            Object.assign(live, copyContourForCheckpoint(edit.before));
            live.invalidated = true;
            if (!live._regionGrow) {
              cornerstoneTools.annotation.state.triggerAnnotationModified(live, edit.element);
            }
            refreshViewerFromAnnotations();
            cornerstoneTools.utilities.triggerAnnotationRender(edit.element);
          },
        });
      };

      // These callbacks start edits to an existing contour, not new strokes.
      for (const name of ["toolSelectedCallback", "handleSelectedCallback"]) {
        const original = this[name].bind(this);
        this[name] = (evt: any, annotation: any, ...rest: any[]) => {
          clear();
          const scope = getActiveScope();
          if (getDefaultStore().get(isSegmentationActiveAtom) && scope) {
            pending = {
              annotation,
              before: copyContourForCheckpoint(annotation),
              element: evt.detail.element,
              scope,
            };
            editingContours.add(annotation);
          }
          try {
            return original(evt, annotation, ...rest);
          } catch (error) {
            clear();
            throw error;
          }
        };
      }
      // Cornerstone's Escape path also calls these completion methods. Record
      // whatever geometry it actually committed; a no-op gets no history entry.
      for (const name of [
        "completeClosedContourEdit",
        "completeOpenContourEdit",
        "completeDrawClosedContour",
        "completeDrawOpenContour",
      ]) {
        const original = this[name].bind(this);
        this[name] = (...args: any[]) => {
          try {
            const result = original(...args);
            finish();
            return result;
          } catch (error) {
            clear();
            throw error;
          }
        };
      }
    }
  };
}
